import * as PropTypes from 'prop-types';
import * as React from 'react';
import { APIRequest, StarRating, UserStore } from '@reverbdotcom/discovery-ui';
import TooltipPanel from '@reverbdotcom/commons/dist/components/tooltip_panel';
import DigitalListing from './digital_listing';
import DownloadButton from './download_button';
import VariantSummary from './show_page/variant_summary';
import AffirmBlurb from './show_page/affirm_blurb';
import Offers from './show_page/offers';
import Fotorama from '../shared/fotorama';
import Tabs from '../shared/tabs';
import ShareItem from '../shared/share_item';
import Breadcrumbs from './breadcrumbs';
import Sale from './sale';
import UrlHelpers from '../shared/url_helpers';
import AddToCartButton from '../listings/cart/standalone_ajax_add_to_cart_button';
import WatchButton from '../listings/watch_button';
import PriceDisplay from '../shared/price_display';
import AcceptedPaymentMethods from '../accepted_payment_methods/accepted_payment_methods';
import ProductReviews from '../product_reviews/product_reviews';
import RelatedContent from '../product_show_page/related_content';

import LoadingPlaceholder from './show_page/loading_placeholder';
import Helmet from 'react-helmet';

const SOFTWARE_CATEGORY = 'Software & Plugins';
const SOLD_OUT = 'sold_out';
const SUSPENDED = 'suspended';
const REVIEWS_PER_PAGE = 3;

class ShowPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      digitalListing: null,
      errorMessage: null,
      successMessage: null,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.fetchDigitalListing();
  }

  fetchDigitalListing() {
    APIRequest.get(
      UrlHelpers.digitalListingPath(this.props.params.digitalListingId),
      { photo_sizes: ['original'] },
      { headers: { 'Accept-Version': '3.0' } },
    ).done(response => this.digitalListingDidLoad(response)).fail(() =>
      this.setState({
        isLoading: false,
      }));
  }

  get document() {
    return this.props.document || window.document;
  }

  download(fileUrl) {
    this.setState({ downloaded: true });
    APIRequest.get(fileUrl).done((response) => {
      if (response.error) {
        this.setState({
          errorMessage: response.error,
          successMessage: null,
          downloaded: false,
        });
      } else {
        this.setState({ errorMessage: null, successMessage: this.successMessageFor(response) });
        const link = this.document.createElement('a');
        if (response.file_name) {
          link.download = response.file_name;
        } else {
          link.target = '_blank';
        }
        link.href = response.url;
        link.className = 'hidden';
        this.document.body.appendChild(link);
        link.click();
      }
    }).fail(() =>
      this.setState({
        errorMessage: I18n.t('digitalListing.error.download'),
        successMessage: null,
        downloaded: false,
      }));
  }

  digitalListingDidLoad(response) {
    const digitalListing = new DigitalListing(response);
    this.setState({
      isLoading: false,
      digitalListing,
    });

    this.trackProductView(digitalListing);
  }

  trackProductView(digitalListing) {
    const url = UrlHelpers.listingViewedPath(digitalListing.product_id);
    APIRequest.post(url);
  }

  downloadUnavailable() {
    const { state } = this.state.digitalListing;
    return state === SOLD_OUT || state === SUSPENDED;
  }

  get free() {
    return this.state.digitalListing.free;
  }

  get studentOnly() {
    return this.state.digitalListing.student_only;
  }

  get cannotGetStudentPrice() {
    return this.studentOnly && !this.state.digitalListing.user_can_get_student_price;
  }

  get compatibilityTabContent() {
    const systemRequirements = this.state.digitalListing.system_requirements;
    if (this.free) {
      return systemRequirements;
    }
    return I18n.t('digitalListing.compatibilityWithRefund', {
      systemRequirements,
      headingOpen: '<h4 class="strong mt-1">',
      headingClose: '</h4>',
      paragraphOpen: '<p class="mtb-0 mlr-0">',
      paragraphClose: '</p>',
    });
  }

  get sale() {
    return this.state.digitalListing.coupon;
  }

  successMessageFor(response) {
    if (response.serial_number && response.file_name) {
      return I18n.t(
        'digitalListing.downloadSuccessWithSerialNumber',
        {
          serialNumber: `<strong class="inline-block">${response.serial_number}</strong>`,
          name: this.state.digitalListing.name,
        },
      );
    } if (response.serial_number && !response.file_name) {
      return I18n.t(
        'digitalListing.externalUrlSuccessWithSerialNumber',
        {
          external_url: `<a
                          href="${response.url}"
                          target="_blank"
                          rel='noreferrer noopener'
                        >
                          ${response.url}
                        </a>`,
          serialNumber: `<strong class="inline-block">${response.serial_number}</strong>`,
          name: this.state.digitalListing.name,
        },
      );
    }
    return I18n.t('digitalListing.downloadSuccess', { name: this.state.digitalListing.name });
  }

  fotoramaImages() {
    return this.state.digitalListing.photos.map(photo => ({
      title: this.state.digitalListing.name,
      href: photo._links.full.href,
      thumb: photo._links.thumbnail.href,
      full: photo._links.original.href,
    }));
  }

  fotoramaVideos() {
    return this.state.digitalListing.videos.map(video => ({
      href: video._links.video.href,
    }));
  }

  shouldRenderPriceDrop() {
    return this.state.digitalListing.price_drop && !this.sale;
  }

  shouldRenderSaleSection() {
    return !this.state.isLoading && this.sale && !this.free;
  }

  reviewTabName() {
    const rating = this.state.digitalListing.reviews.rating;
    const total = this.state.digitalListing.reviews.review_count;

    if (rating) {
      return (
        <StarRating
          count={total}
          rating={rating / 5}
        />
      );
    }

    return I18n.t('digitalListing.noReviews');
  }

  renderLoadError() {
    return (
      <div className="error-page-content">
        <div className="content-frame pb-6 align-center">
          <div className="mb-1">
            <h3 className="heading-3 halfmargined-bottom">
              {I18n.t('digitalListing.error.title')}
            </h3>
            <h3 className="heading-3">
              {I18n.t('digitalListing.error.explanation')}
            </h3>
          </div>

          <ul className="inline-list pb-6">
            <li className="extramargined-bottom">
              <a href={UrlHelpers.softwarePath()} className="btn btn-blue">
                {I18n.t('digitalListing.error.softwarePage')}
              </a>
            </li>
            <li className="ml-half">
              <a href="/page/contact" className="btn">
                {I18n.t('digitalListing.error.contactSupport')}
              </a>
            </li>
          </ul>
        </div>
      </div>
    );
  }


  renderAdminButton() {
    if (!UserStore.getState().isAdmin) {
      return null;
    }

    return (
      <div className="content-frame clearfix">
        <div className="pull-right margined-top">
          <a
            className="btn btn-warning uppercase"
            href={`/admin/digital_listings/${this.props.params.digitalListingId}`}
          >
            admin
          </a>
        </div>
      </div>
    );
  }

  renderTitle() {
    if (this.state.digitalListing) {
      return <Helmet title={this.state.digitalListing.name} />;
    }

    return null;
  }

  renderBreadCrumbs() {
    const breadcrumbs = [
      {
        url: UrlHelpers.rootPath,
        name: I18n.t('digitalListing.home'),
        clientSide: false,
      },
      {
        url: UrlHelpers.softwarePath(),
        name: I18n.t('digitalListing.softwareCategoryName'),
        clientSide: true,
      },
      {
        url: UrlHelpers.softwarePath(this.props.params.categorySlug),
        name: this.state.digitalListing.category.name,
        clientSide: true,
      },
      {
        url: this.state.digitalListing._links.web.href,
        name: this.state.digitalListing.name,
        clientSide: true,
      },
    ];

    return <Breadcrumbs breadcrumbs={breadcrumbs} />;
  }

  renderMetaData() {
    return (
      <div>
        <meta itemProp="url" content={this.state.digitalListing._links.web.href} />
        <meta itemProp="brand" content={this.state.digitalListing.brand} itemType="http://schema.org/Organization" />
        <div itemScope itemProp="offers" itemType="http://schema.org/Offer">
          <meta itemProp="http://schema.org/availability" content="http://schema.org/InStock" />
          <meta itemProp="http://schema.org/price" content={this.state.digitalListing.price.amount} />
          <meta itemProp="http://schema.org/priceCurrency" content={this.state.digitalListing.price.currency} />
          <meta itemProp="http://schema.org/category" content={SOFTWARE_CATEGORY} />
        </div>
      </div>
    );
  }

  renderProductDetail() {
    return (
      <Tabs className="tabbable">
        {this.renderDescriptionTab()}
        {this.renderTab(
          this.state.digitalListing.release_notes,
          I18n.t('digitalListing.whatsNew'),
          'whats-new',
        )}
        {this.renderTab(
          this.compatibilityTabContent,
          I18n.t('digitalListing.compatibility'),
          'system-requirements',
        )}
        {this.renderTab(
          this.state.digitalListing.installation_instructions,
          I18n.t('digitalListing.installation'),
          'installation-instructions',
        )}
      </Tabs>
    );
  }

  renderDescriptionTab() {
    const description = (
      <div>
        <hr className="mtb-1" />
        <div
          dangerouslySetInnerHTML={this.renderRichText(this.state.digitalListing.description)}
          itemProp="description"
        />
      </div>
    );

    return (
      <Tabs.Tab name={I18n.t('digitalListing.description')} id="description">
        <div className="product-detail-formatted">
          <h1 className="strong">{I18n.t('digitalListing.summary')}</h1>
          <div dangerouslySetInnerHTML={this.renderRichText(this.state.digitalListing.summary)} />
          {this.state.digitalListing.description ? description : null}
        </div>
      </Tabs.Tab>
    );
  }

  renderTab(text, title, id, itemProp = null) {
    if (!text) { return false; }
    return (
      <Tabs.Tab name={title} id={id}>
        <div
          className="product-detail-formatted"
          dangerouslySetInnerHTML={this.renderRichText(text)}
          itemProp={itemProp}
        />
      </Tabs.Tab>
    );
  }

  renderReviews() {
    const rating = this.state.digitalListing.reviews.rating;
    const total = this.state.digitalListing.reviews.review_count;

    return (
      <section className="module scaling-mt-2">
        <div className="offset-anchor-wrapper">
          <div id="reviews-section" className="offset-anchor" />
        </div>
        {total > 0 &&
          <div
            className="d-none"
            itemProp="aggregateRating"
            itemScope
            itemType="http://schema.org/AggregateRating"
          >
            <meta content={rating} itemProp="ratingValue" />
            <meta content="5" itemProp="bestRating" />
            <meta content="0" itemProp="worstRating" />
            <meta content={total} itemProp="reviewCount" />
          </div>
        }
        <ProductReviews
          rating={rating}
          reviewLink={UrlHelpers.listingProductReviewsPath(this.state.digitalListing.product_id)}
          headingClass="heading-3 normal"
          canReview={this.state.digitalListing.reviews.can_review}
          total={total}
          productTitle={this.state.digitalListing.name}
          formOpenByDefault={false}
          sectionSelector="#reviews-tab"
          numberPerPage={REVIEWS_PER_PAGE}
        />
      </section>
    );
  }

  renderRichText(text) {
    return { __html: text };
  }

  renderMessage() {
    if (this.state.successMessage) {
      return (
        <p
          className="alert alert-success break-word"
          dangerouslySetInnerHTML={{ __html: this.state.successMessage }}
        />
      );
    } if (this.state.errorMessage) {
      return <p className="alert alert-error">{this.state.errorMessage}</p>;
    }
    return false;
  }

  renderStudentsOnlyMessage() {
    return (
      <div>
        <div className="btn btn-primary download-btn btn-disabled wide">
          {I18n.t('productShowPage.addToCart')}
        </div>
        <div className="mt-1 small muted align-center">
          {I18n.t('digitalListing.notAStudent')}
        </div>
      </div>
    );
  }

  renderAction() {
    if (this.downloadUnavailable()) {
      return (
        <div className="btn btn-primary download-btn btn-disabled wide">
          {I18n.t('digitalListing.noFilesFound')}
        </div>
      );
    }

    if (this.cannotGetStudentPrice) {
      return (
        <div>
          {this.renderStudentsOnlyMessage()}
        </div>
      );
    }

    if (this.free) {
      return (
        <DownloadButton
          variants={this.state.digitalListing.variants}
          downloadClicked={fileUrl => this.download(fileUrl)}
          downloaded={this.state.downloaded}
        />
      );
    }

    return (
      <AddToCartButton
        productId={this.state.digitalListing.product_id}
        className="btn btn-primary wide"
      />
    );
  }

  renderOffers() {
    if (!this.state.digitalListing.offers_enabled) { return null; }
    if (this.downloadUnavailable()) { return null; }

    return (
      <Offers
        productId={this.state.digitalListing.product_id}
        productCurrency={this.state.digitalListing.original_currency}
      />
    );
  }

  renderAcceptedPaymentMethods() {
    if (this.free) { return false; }

    return (
      <div className="mtb-1">
        <AcceptedPaymentMethods
          acceptedPaymentMethods={this.state.digitalListing.accepted_payment_methods}
        />
        <AffirmBlurb digitalListing={this.state.digitalListing} />
        <div className="module module-footer align-center">
          <p className="small muted mb-0">{I18n.t('digitalListing.refundPolicy')}</p>
        </div>
      </div>
    );
  }

  renderPriceSection() {
    if (this.studentOnly) {
      return this.renderStudentPriceSection();
    }

    if (this.shouldRenderPriceDrop()) {
      return this.renderPriceDropSection();
    }

    return this.renderPrice();
  }

  renderStudentPriceSection() {
    return (
      <div>
        {this.renderOriginalPrice()}
        <div className="uppercase small normal mt-half color-red">
          {I18n.t('digitalListing.studentPrice')}
        </div>
        <div className="listing-price__price-dropped">
          {this.renderPrice()}
        </div>
      </div>
    );
  }

  renderPriceDropSection() {
    return (
      <div>
        {this.renderOriginalPrice()}
        <div className="listing-price__price-dropped">
          {this.renderPrice()}
        </div>
      </div>
    );
  }

  renderPrice() {
    return <PriceDisplay price={this.state.digitalListing.price} showFree />;
  }

  renderOriginalPrice() {
    if (this.state.digitalListing.original_price) {
      return (
        <div className="listing-price__strike">
          {this.state.digitalListing.original_price.display}
        </div>
      );
    }

    return null;
  }

  renderPriceDropBanner() {
    if (this.shouldRenderPriceDrop() && !this.studentOnly) {
      return (
        <h2 className="ribbon sale-ribbon">
          {this.state.digitalListing.price_drop.display}
        </h2>
      );
    }

    return null;
  }

  renderSupportedFormats() {
    return this.state.digitalListing.variants.map((variant, index) =>
      <VariantSummary variant={variant} key={index} />);
  }

  renderWatchersCount() {
    if (this.state.digitalListing.watchers_count === 0) { return null; }

    return (
      <div className="product-watch-offer-stats muted margined-top align-center small">
        <span className="icon-l-star-o">
          {I18n.t(
            'digitalListing.watchers',
            { count: this.state.digitalListing.watchers_count },
          )}
        </span>
      </div>
    );
  }

  renderSaleSection() {
    if (!this.shouldRenderSaleSection()) {
      return null;
    }

    return (
      <Sale
        sale={this.sale}
      />
    );
  }

  renderDownloadCount() {
    if (!this.state.digitalListing.free) { return null; }

    return (
      <li>
        {I18n.t(
          'digitalListing.downloads',
          { count: this.state.digitalListing.downloads },
        )}
      </li>
    );
  }

  render() {
    if (this.state.isLoading) {
      return <LoadingPlaceholder />;
    }

    if (!this.state.digitalListing) { return this.renderLoadError(); }

    return (
      <div>
        {this.renderTitle()}
        {this.renderAdminButton()}
        <div className="digital-listing-page products show" itemScope itemType="http://schema.org/Product">
          <div className="site-wrapper site-wrapper--narrow">
            {this.renderMetaData()}
            {this.renderBreadCrumbs()}
            <section className="mt-2 mb-1 page-header">
              <h1 className="heading-1 product-title" itemProp="name">
                {this.state.digitalListing.name}
              </h1>
              <div className="horizontal-nav">
                <ul>
                  <li>
                    {I18n.t('digitalListing.createdBy')}
                    {' '}
                    <a
                      href={UrlHelpers.brandPath(this.props.params.brandSlug)}
                    >
                      {this.state.digitalListing.brand}
                    </a>
                  </li>
                  <li className="show-only-on-desktop">
                    {I18n.t(
                      'digitalListing.views',
                      { count: this.state.digitalListing.views },
                    )}
                  </li>
                  {this.renderDownloadCount()}
                </ul>
              </div>
            </section>
            <div className="g-container">
              <div className="g-col-8 g-col-mobile-12">
                <div className="digital-listing-gallery mb-2 mobile-mb-1" id="gallery">
                  {this.renderPriceDropBanner()}
                  <Fotorama
                    nav="thumbs"
                    height="350"
                    images={this.fotoramaImages()}
                    videos={this.fotoramaVideos()}
                  />
                </div>
                <section className="description-section mb-3 show-only-on-desktop">
                  {this.renderProductDetail()}
                  <ShareItem
                    shareLinks={this.state.digitalListing._links}
                  />
                  {this.renderReviews()}
                </section>
              </div>
              <div className="g-col-4 g-col-mobile-12">
                {this.renderSaleSection()}
                <section className="mb-1">
                  <div className="module btm-square-module bd-rounded">
                    <div className="mb-1 sidebar-price-module mt-half">
                      <div className="listing-price">
                        {this.renderPriceSection()}
                      </div>
                    </div>
                    {this.renderMessage()}
                    {this.renderAction()}
                    {this.renderOffers()}
                    <p className="fine-print align-center mt-1 mb-0">
                      <TooltipPanel
                        text={I18n.t('digitalListing.fulfilledByReverbTooltip')}
                        type="plain"
                      >
                        {I18n.t('digitalListing.fulfilledByReverb')}
                        <span className="reverb-digital-download-icon ml-half" />
                      </TooltipPanel>
                    </p>
                    {this.renderWatchersCount()}
                  </div>
                  <div className="module module-footer align-center">
                    <WatchButton
                      productId={this.state.digitalListing.product_id}
                      isWatching={this.state.digitalListing.in_watchlist}
                      productImage={this.state.digitalListing.photos[0]._links.thumbnail.href}
                      sourceProductUuid={this.state.digitalListing.category.uuid}
                    />
                  </div>
                </section>

                {this.renderAcceptedPaymentMethods()}

                <section className="mb-1">
                  <div className="module bd-rounded">
                    <h1 className="uppercase fine-print mb-1 mobile-mt-half align-center">
                      {I18n.t('digitalListing.supportedFormats')}
                    </h1>
                    {this.renderSupportedFormats()}
                  </div>
                </section>

                <section className="description-section mb-1 show-only-on-mobile">
                  {this.renderProductDetail()}
                  <ShareItem
                    shareLinks={this.state.digitalListing._links}
                  />
                </section>
              </div>
            </div>
          </div>
          <div className="full-width-footer full-width-footer__dark mt-2 ptb-half">
            <RelatedContent listingId={this.state.digitalListing.product_id.toString()} />
          </div>
        </div>
      </div>
    );
  }
}

ShowPage.propTypes = {
  params: PropTypes.object,
  document: PropTypes.object,
};


export default ShowPage;



// WEBPACK FOOTER //
// ./javascripts/components/digital_listings/show_page.jsx